import React, {useEffect, useRef, useState} from "react";
import {XhrHttpModule} from "@intuitionrobotics/thunderstorm/frontend";
import {BeLogged, LogLevel} from "@intuitionrobotics/ts-common";
import {BaseHttpRequest} from "@intuitionrobotics/thunderstorm";
import VersionUpdateBanner from "./VersionUpdateBanner";
import VersionUpdateModal from "./VersionUpdateModal";
import {config as appConfig} from "../../../config"

const config = {
    enabled: true,
    inactivityTimeout: 300000,  // 5 minutes
    checkInterval: 60000,       // 1 minute
    ignoredEnvironments: ["local"] as string[]    // Can add ['dev', 'local'] to disable
}

const LOG_TAG = 'VersionUpdate';
const log = (logLevel: LogLevel, msg: string) => BeLogged.log(LOG_TAG, logLevel, false, msg);
const logVerbose = (msg: string) => log(LogLevel.Verbose, msg);
const logInfo = (msg: string) => log(LogLevel.Info, msg);
const logWarning = (msg: string) => log(LogLevel.Warning, msg);
const isEnabled = (): boolean => {
    if (!config.enabled) return false;

    // Check if current environment should be ignored
    const envName = appConfig.EnvironmentModule.envName || 'local';
    return !config.ignoredEnvironments?.includes(envName);
};

export enum NotificationLevel {
    NONE = 'none',
    SUBTLE = 'subtle',      // Non-blocking banner
    AGGRESSIVE = 'aggressive' // Modal dialog
}

const currentVersion = process.env.appVersion || '0.0.0'

export type ReleaseNotesGroup = { version: string, notes: string[] }

// Numeric compare of two "MAJOR.MINOR.PATCH" version strings.
const compareVersions = (a: string, b: string): number => {
    const pa = a.split('.').map(n => parseInt(n, 10) || 0);
    const pb = b.split('.').map(n => parseInt(n, 10) || 0);
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
        const diff = (pa[i] || 0) - (pb[i] || 0);
        if (diff !== 0) return diff;
    }
    return 0;
};

// Everything the running client has missed: notes for every version released
// after `fromVersion`, up to and including the latest `toVersion`, newest first.
// The client already has the full map from the static file, so it composes the
// range locally — no per-version API call needed.
const collectNotesSince = (
    all: Record<string, string[]>,
    fromVersion: string,
    toVersion: string
): ReleaseNotesGroup[] =>
    Object.keys(all)
        .filter(v => compareVersions(v, fromVersion) > 0 && compareVersions(v, toVersion) <= 0)
        .sort((a, b) => compareVersions(b, a))
        .map(v => ({version: v, notes: all[v]}))
        .filter(group => Array.isArray(group.notes) && group.notes.length > 0);

const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
export const VersionUpdate = () => {

    const [mismatchDetected, setMismatchDetected] = useState(false)
    const [notificationLevel, setNotificationLevel] = useState(NotificationLevel.NONE)
    const [notificationDismissed, setNotificationDismissed] = useState(false)
    const [serverVersion, setServerVersion] = useState("")
    const [releaseNotes, setReleaseNotes] = useState<ReleaseNotesGroup[] | null>(null)

    // Refs to avoid stale closures in registered callbacks
    const serverVersionRef = useRef("")
    const mismatchDetectedRef = useRef(false)
    const notificationLevelRef = useRef(NotificationLevel.NONE)
    const lastActivityTimeRef = useRef(Date.now())

    const eventListener = () => { lastActivityTimeRef.current = Date.now(); };

    useEffect(() => {
        if (!isEnabled()) {
            logInfo(`${LOG_TAG} disabled`);
            return;
        }

        logInfo(`${LOG_TAG} initialized. Current version: ${currentVersion}`);

        // Set up activity tracking
        activityEvents.forEach(event => {
            window.addEventListener(event, eventListener, {passive: true});
        });

        // Start periodic inactivity checker
        const intervalId = setInterval(() => {
            if (!mismatchDetectedRef.current) return;

            const inactiveTime = Date.now() - lastActivityTimeRef.current;

            // Escalate notification level based on inactivity
            if (inactiveTime >= config.inactivityTimeout) {
                if (notificationLevelRef.current === NotificationLevel.SUBTLE) {
                    notificationLevelRef.current = NotificationLevel.AGGRESSIVE;
                    setNotificationLevel(NotificationLevel.AGGRESSIVE)
                    setNotificationDismissed(false)
                    logInfo('Escalating notification to aggressive (user inactive)')
                }
            }
        }, config.checkInterval);

        XhrHttpModule.addDefaultResponseHandler((request: BaseHttpRequest<any>) => {
            // CRITICAL: this default handler runs after every request. If it
            // throws, processDefaultResponseHandlers aborts before the user's
            // onSuccess callback fires — so the caller's awaited Promise hangs
            // forever. The PII disposal flow has hit this twice now (a hung
            // refurbish-som call wedging the entire orchestration), so guard
            // every interaction with the request object below.
            let status: number;
            try {
                status = request.getStatus();
            } catch {
                return false;
            }
            if (status >= 400)
                return false;

            let responseServerVersion: string | undefined;
            try {
                responseServerVersion = request.getResponseHeader('X-App-Version');
            } catch {
                // XhrHttpModule occasionally throws "xhr didn't return yet"
                // here — race in the underlying XHR state machine. Skip this
                // invocation; we'll see the version on a later request.
                return false;
            }

            logVerbose(`Detected server version: ${responseServerVersion}`);
            logVerbose(`Current version: ${currentVersion}`);

            // First request - just initialize, don't check
            if (!serverVersionRef.current) {
                serverVersionRef.current = responseServerVersion || currentVersion;
                setServerVersion(serverVersionRef.current);
                return false;
            }

            // Subsequent requests - check for version mismatch
            if (responseServerVersion && serverVersionRef.current !== responseServerVersion) {
                serverVersionRef.current = responseServerVersion;
                setServerVersion(responseServerVersion);

                if (currentVersion !== responseServerVersion && !mismatchDetectedRef.current) {
                    mismatchDetectedRef.current = true;
                    notificationLevelRef.current = NotificationLevel.SUBTLE;
                    logWarning(`Version mismatch detected! Current: ${currentVersion}, Server: ${responseServerVersion}`);
                    setMismatchDetected(true)
                    setNotificationLevel(NotificationLevel.SUBTLE)
                    setNotificationDismissed(false)
                    logInfo('Version mismatch detected - notifying user');
                }
            }
            // Ensures all handlers get a chance to process
            return false;
        })

        return () => {
            clearInterval(intervalId);
            activityEvents.forEach(event => {
                window.removeEventListener(event, eventListener);
            });
        }
    }, [])

    // Fetch human-readable release notes. The running client is the OLD build,
    // so it has no knowledge of what changed — the full version->notes map is
    // served as a static file by the (already-updated) hosting, and the client
    // composes every version it has missed (from its own version up to the
    // latest). Best-effort: on any failure (older deploy without the file,
    // offline, SPA rewrite returning index.html) we silently keep the
    // version-number-only display.
    useEffect(() => {
        if (!mismatchDetected || !serverVersion) return;

        let cancelled = false;
        fetch(`/release-notes.json?v=${encodeURIComponent(serverVersion)}`, {cache: 'no-store'})
            .then(res => res.ok ? res.json() : null)
            .then((data: Record<string, string[]> | null) => {
                if (cancelled || !data) return;
                const groups = collectNotesSince(data, currentVersion, serverVersion);
                setReleaseNotes(groups.length > 0 ? groups : null);
            })
            .catch(() => { /* no notes available — fall back to version numbers */ });

        return () => { cancelled = true; };
    }, [mismatchDetected, serverVersion])

    const dismissNotification = (): void => {
        notificationLevelRef.current = NotificationLevel.SUBTLE;
        setNotificationDismissed(true)
        setNotificationLevel(NotificationLevel.SUBTLE)
        logVerbose('Notification dismissed by user')
    };

    const forceReload = (): void => {
        logVerbose('Force reloading application...')
        window.location.reload();
    };

    return <>
        <VersionUpdateBanner
            mismatchDetected={mismatchDetected}
            dismissed={notificationDismissed}
            notificationLevel={notificationLevel}
            releaseNotes={releaseNotes}
            forceReload={forceReload}
            dismissNotification={dismissNotification}
        />
        <VersionUpdateModal
            dismissed={notificationDismissed}
            notificationLevel={notificationLevel}
            mismatchDetected={mismatchDetected}
            serverVersion={serverVersion}
            currentVersion={currentVersion}
            releaseNotes={releaseNotes}
            forceReload={forceReload}
            dismissNotification={dismissNotification}
        />
    </>
}